import { __assign, __read } from "tslib";
import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
/** Session cache: URLs that have successfully loaded (survives list unmount/remount). */
var loadedImageUrls = new Set();
export function isBrandedRevealImageCached(src) {
    return loadedImageUrls.has(src);
}
var variantClass = function (variant) { return "branded-reveal--".concat(variant); };
function getScrollParent(node) {
    var current = node ? node.parentElement : null;
    while (current) {
        var style = window.getComputedStyle(current);
        var overflowY = style.overflowY;
        var overflow = style.overflow;
        if (overflowY === 'auto' ||
            overflowY === 'scroll' ||
            overflowY === 'overlay' ||
            overflow === 'auto' ||
            overflow === 'scroll') {
            return current;
        }
        current = current.parentElement;
    }
    return null;
}
function isWithinVisibleArea(shell, scrollRoot, thresholdPx) {
    var rect = shell.getBoundingClientRect();
    if (rect.width <= 0 && rect.height <= 0) {
        return false;
    }
    if (!scrollRoot) {
        return rect.top < window.innerHeight + thresholdPx && rect.bottom > -thresholdPx;
    }
    var rootRect = scrollRoot.getBoundingClientRect();
    return (rect.bottom >= rootRect.top - thresholdPx &&
        rect.top <= rootRect.bottom + thresholdPx &&
        rect.right >= rootRect.left &&
        rect.left <= rootRect.right);
}
export var BrandedRevealImage = React.forwardRef(function (_a, forwardedRef) {
    var src = _a.src, alt = _a.alt, _b = _a.variant, variant = _b === void 0 ? 'list' : _b, _c = _a.imageClassName, imageClassName = _c === void 0 ? '' : _c, _d = _a.shellClassName, shellClassName = _d === void 0 ? '' : _d, onLoad = _a.onLoad, onError = _a.onError, onClick = _a.onClick, _e = _a.lazy, lazy = _e === void 0 ? false : _e, _f = _a.threshold, threshold = _f === void 0 ? 300 : _f, _g = _a.visibleByDefault, visibleByDefault = _g === void 0 ? false : _g, _h = _a.decoding, decoding = _h === void 0 ? 'async' : _h, dataThumbIndex = _a.dataThumbIndex;
    var _j = __read(useState(function () { return loadedImageUrls.has(src); }), 2), loaded = _j[0], setLoaded = _j[1];
    var _k = __read(useState(function () { return !lazy || visibleByDefault || loadedImageUrls.has(src); }), 2), shouldLoad = _k[0], setShouldLoad = _k[1];
    var shellRef = useRef(null);
    var internalRef = useRef(null);
    var loadNotifiedRef = useRef(false);
    var onLoadRef = useRef(onLoad);
    var onErrorRef = useRef(onError);
    onLoadRef.current = onLoad;
    onErrorRef.current = onError;
    var setImgRef = useCallback(function (node) {
        internalRef.current = node;
        if (typeof forwardedRef === 'function') {
            forwardedRef(node);
        }
        else if (forwardedRef) {
            forwardedRef.current = node;
        }
    }, [forwardedRef]);
    var notifyLoaded = useCallback(function () {
        var _a;
        if (loadNotifiedRef.current) {
            return;
        }
        loadNotifiedRef.current = true;
        (_a = onLoadRef.current) === null || _a === void 0 ? void 0 : _a.call(onLoadRef);
    }, []);
    useEffect(function () {
        var cached = loadedImageUrls.has(src);
        setLoaded(cached);
        loadNotifiedRef.current = cached;
    }, [src]);
    useLayoutEffect(function () {
        if (!loadedImageUrls.has(src)) {
            return;
        }
        setLoaded(true);
        if (lazy) {
            setShouldLoad(true);
        }
        notifyLoaded();
    }, [src, lazy, notifyLoaded]);
    useEffect(function () {
        if (!lazy) {
            setShouldLoad(true);
            return;
        }
        if (visibleByDefault || loadedImageUrls.has(src)) {
            setShouldLoad(true);
            return;
        }
        var shell = shellRef.current;
        if (!shell) {
            return;
        }
        var scrollRoot = getScrollParent(shell);
        // Already on screen — load without toggling shouldLoad off (avoids update loops with many tiles).
        if (isWithinVisibleArea(shell, scrollRoot, threshold)) {
            setShouldLoad(true);
            return;
        }
        setShouldLoad(false);
        var cancelled = false;
        var observer = null;
        var reveal = function () {
            if (cancelled) {
                return;
            }
            cancelled = true;
            observer === null || observer === void 0 ? void 0 : observer.disconnect();
            setShouldLoad(true);
        };
        var observerRoot = scrollRoot &&
            scrollRoot !== document.documentElement &&
            scrollRoot !== document.body
            ? scrollRoot
            : null;
        observer = new IntersectionObserver(function (entries) {
            if (entries.some(function (entry) { return entry.isIntersecting; })) {
                reveal();
            }
        }, { root: observerRoot, rootMargin: "".concat(threshold, "px 0px"), threshold: 0 });
        observer.observe(shell);
        return function () {
            cancelled = true;
            observer === null || observer === void 0 ? void 0 : observer.disconnect();
        };
    }, [lazy, src, threshold, visibleByDefault]);
    var markLoaded = useCallback(function () {
        loadedImageUrls.add(src);
        setLoaded(true);
        notifyLoaded();
    }, [src, notifyLoaded]);
    var handleError = useCallback(function () {
        var _a;
        setLoaded(true);
        notifyLoaded();
        (_a = onErrorRef.current) === null || _a === void 0 ? void 0 : _a.call(onErrorRef);
    }, [notifyLoaded]);
    useEffect(function () {
        var el = internalRef.current;
        if (el instanceof HTMLImageElement && el.complete && el.naturalWidth > 0) {
            loadedImageUrls.add(src);
            setLoaded(true);
            notifyLoaded();
        }
    }, [src, shouldLoad, notifyLoaded]);
    var shellClass = [
        'branded-reveal',
        variantClass(variant),
        shellClassName,
        loaded ? 'branded-reveal--loaded' : '',
    ]
        .filter(Boolean)
        .join(' ');
    var placeholderClass = 'branded-reveal__placeholder' +
        (loaded ? ' branded-reveal__placeholder--loaded' : '');
    var imgClass = [
        'branded-reveal__img',
        loaded ? 'branded-reveal__img--loaded' : '',
        imageClassName,
    ]
        .filter(Boolean)
        .join(' ');
    return (React.createElement("div", { ref: shellRef, className: shellClass },
        React.createElement("div", { className: placeholderClass, "aria-hidden": "true" }),
        lazy ? (shouldLoad ? (React.createElement("img", { ref: setImgRef, alt: alt, src: src, className: imgClass, decoding: decoding, onClick: onClick, onLoad: markLoaded, onError: handleError })) : (React.createElement("span", { className: "branded-reveal__lazy-wrap", "aria-hidden": "true" }))) : (React.createElement("img", __assign({ ref: setImgRef, alt: alt, src: src, className: imgClass, decoding: decoding, onClick: onClick, onLoad: markLoaded, onError: handleError }, (dataThumbIndex !== undefined
            ? { 'data-thumb-index': String(dataThumbIndex) }
            : {}))))));
});
BrandedRevealImage.displayName = 'BrandedRevealImage';
export default BrandedRevealImage;
//# sourceMappingURL=BrandedRevealImage.js.map